/**
 * Le regroupement du catalogue public.
 *
 * Séparé du composant pour deux raisons. La première est technique : un fichier
 * qui exporte à la fois un composant et des fonctions casse le rafraîchissement
 * à chaud de Vite. La seconde vaut mieux — c'est la seule logique de cette
 * page, donc la seule qui puisse se tromper, et elle s'éprouve ici sans monter
 * de composant.
 */

import {
  NOM_DU_TYPE_PLURIEL,
  commune,
  prixEnEuros,
  typeDe,
  type StagePublic,
  type TypeDActivite,
} from './stage';

/** Période puis commune : l'ordre dans lequel une famille cherche. */
export function grouperParPeriode(
  stages: StagePublic[],
): { titre: string; lignes: StagePublic[] }[] {
  const groupes = new Map<string, { titre: string; lignes: StagePublic[] }>();
  for (const s of stages) {
    const cle = [s.periode, s.annee].filter(Boolean).join(' ') || 'Prochainement';
    let g = groupes.get(cle);
    if (!g) {
      g = { titre: cle, lignes: [] };
      groupes.set(cle, g);
    }
    g.lignes.push(s);
  }
  return [...groupes.values()];
}

/** Les communes citées, dédoublonnées et triées — elles servent les recherches locales. */
export function communesDesStages(stages: StagePublic[]): string[] {
  return [...new Set(stages.map((s) => commune(s)))].sort((a, b) => a.localeCompare(b, 'fr'));
}

/**
 * Dans une période, les activités rangées par type.
 *
 * L'ordre est fixe — plaines, stages, séjours — et non celui des données : une
 * section qui change de place d'un déploiement à l'autre brouille autant le
 * lecteur que le moteur. Les plaines d'abord parce que ce sont elles qui
 * accueillent le plus d'enfants.
 */
const ORDRE: TypeDActivite[] = ['plaine', 'stage', 'sejour'];

export function grouperParType(
  stages: StagePublic[],
): { type: TypeDActivite; titre: string; lignes: StagePublic[] }[] {
  return ORDRE.map((type) => ({
    type,
    titre: NOM_DU_TYPE_PLURIEL[type],
    lignes: stages.filter((s) => typeDe(s) === type),
  })).filter((g) => g.lignes.length > 0);
}

/**
 * Un fragment d'adresse à partir d'un libellé.
 *
 * Mêmes règles que `programme_slug` en base — sans accent, sans ponctuation,
 * en minuscules — mais pour les adresses de commune et de période, qui sont
 * calculées à la volée et non stockées. Les deux implémentations existent parce
 * que les deux côtés en ont besoin sans pouvoir s'appeler l'un l'autre ; un
 * test compare leurs résultats sur les libellés réels.
 */
export function slugifier(texte: string): string {
  return texte
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

export interface Regroupement {
  /** Ce qui s'affiche : « Ixelles », « Automne 2026 ». */
  nom: string;
  /** Le dernier segment de l'adresse : « ixelles », « automne-2026 ». */
  slug: string;
  stages: StagePublic[];
}

/**
 * Les communes où il se passe quelque chose, chacune avec ses activités.
 *
 * Une commune sans activité publique n'a pas de page : une page vide est
 * exactement ce que le cahier des charges écarte (§14), et elle diluerait le
 * reste du site plutôt que de l'aider.
 */
export function communesAvecStages(stages: StagePublic[]): Regroupement[] {
  const par = new Map<string, Regroupement>();
  for (const s of stages) {
    const nom = commune(s);
    const slug = slugifier(nom);
    if (!slug) continue;
    let g = par.get(slug);
    if (!g) {
      g = { nom, slug, stages: [] };
      par.set(slug, g);
    }
    g.stages.push(s);
  }
  return [...par.values()].sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
}

/**
 * Les périodes de vacances qui portent des activités.
 *
 * L'adresse vient de `periode_code` et de l'année — « automne-2026 » — et non
 * d'un libellé slugifié : le code est stable en base, le libellé peut être
 * réécrit un jour, et une adresse ne doit pas bouger.
 *
 * Les périodes sortent dans l'ordre du calendrier, calculé sur la date de
 * début la plus précoce de chaque groupe. Trier sur la première ligne reçue
 * aurait donné le même résultat tant que l'appelant passe les lignes triées —
 * ce que fait la passerelle — mais c'est un contrat implicite qu'un appelant
 * futur casserait sans rien voir.
 */
export function periodesAvecStages(stages: StagePublic[]): Regroupement[] {
  const par = new Map<string, Regroupement>();
  for (const s of stages) {
    if (!s.periode_code || !s.annee) continue;
    const slug = `${slugifier(s.periode_code)}-${s.annee}`;
    let g = par.get(slug);
    if (!g) {
      g = { nom: `${s.periode ?? s.periode_code} ${s.annee}`, slug, stages: [] };
      par.set(slug, g);
    }
    g.stages.push(s);
  }
  const debut = (r: Regroupement) =>
    r.stages.reduce((tot, s) => (s.start_date < tot ? s.start_date : tot), '9999-12-31');
  return [...par.values()].sort((a, b) => debut(a).localeCompare(debut(b)));
}

/**
 * Une activité, et toutes les semaines où on peut la vivre.
 *
 * Le catalogue listait les SESSIONS : 67 lignes en production pour 10 stages,
 * c'est-à-dire « Les Aigles 9 - 13 ans » répété dix fois, une par commune et
 * par semaine, avec la même photo et la même description. Un visiteur lisait
 * dix fois la même chose avant d'arriver à la suivante.
 *
 * Il liste maintenant les ACTIVITÉS, chacune portant ses séances. Rien n'est
 * perdu pour les moteurs : chaque séance garde son lien et sa page, elle est
 * simplement rangée sous l'activité dont elle relève, ce qui est la façon dont
 * une famille y pense — « est-ce que vous faites du vélo ? », et ensuite
 * seulement « où et quand ? ».
 */
export interface Activite {
  /** Le titre du stage, tel qu'il est saisi. */
  nom: string;
  type: TypeDActivite;
  /** La première séance, qui sert de représentante : photo, description, âges. */
  vitrine: StagePublic;
  /** Toutes les séances, dans l'ordre où elles commencent. */
  seances: StagePublic[];
  /** Les communes citées une fois chacune, pour la ligne de résumé. */
  communes: string[];
  /** Le prix le plus bas — « à partir de », quand ils diffèrent. */
  prixMin: number;
  prixMax: number;
}

export function grouperParActivite(stages: StagePublic[]): Activite[] {
  const par = new Map<string, StagePublic[]>();
  for (const s of stages) {
    const cle = s.stage;
    const deja = par.get(cle);
    if (deja) deja.push(s);
    else par.set(cle, [s]);
  }

  return [...par.values()]
    .map((seances) => {
      const triees = [...seances].sort((a, b) => a.start_date.localeCompare(b.start_date));
      const vitrine = triees[0];
      const prix = triees.map((s) => s.prix_normal);
      return {
        nom: vitrine.stage,
        type: typeDe(vitrine),
        vitrine,
        seances: triees,
        communes: [...new Set(triees.map((s) => commune(s)))],
        prixMin: Math.min(...prix),
        prixMax: Math.max(...prix),
      };
    })
    /*
      L'ordre fixe des types d'abord — plaines, stages, séjours — puis la date
      de la première séance. Une famille qui revient doit retrouver la page
      dans le même état, et non dans l'ordre où la base a répondu.
    */
    .sort((a, b) => {
      const parType = ORDRE.indexOf(a.type) - ORDRE.indexOf(b.type);
      if (parType !== 0) return parType;
      return a.vitrine.start_date.localeCompare(b.vitrine.start_date);
    });
}

/**
 * Les faits d'une activité en une ligne : où, quand, à partir de combien.
 *
 * Compacte plutôt que rédigée. La carte en porte une par activité, et dix
 * phrases complètes les unes sous les autres redeviendraient le mur de texte
 * qu'on vient de défaire — alors que ces trois faits-là sont ceux qu'on
 * compare d'un coup d'œil.
 */
export function ouEtQuand(a: Activite): string {
  const lieux =
    a.communes.length === 1
      ? `à ${a.communes[0]}`
      : `dans ${a.communes.length} communes`;

  const semaines = new Set(a.seances.map((s) => s.start_date)).size;
  const quand = `${semaines} semaine${semaines > 1 ? 's' : ''}`;

  const prix =
    a.prixMin === a.prixMax ? prixEnEuros(a.prixMin) : `dès ${prixEnEuros(a.prixMin)}`;

  return `${lieux} · ${quand} · ${prix}`;
}

/**
 * Une séance nommée comme on la choisit : le lieu, puis la semaine.
 *
 * `periodeEnToutesLettres` rend « du 19 au 23 octobre 2026 », ce qui est juste
 * mais long quand dix séances se suivent. Ici la commune vient en premier,
 * parce que c'est par elle qu'une famille tranche.
 */
export function libelleDeSeance(s: StagePublic): string {
  const d = new Date(`${s.start_date}T12:00:00Z`);
  const jour = new Intl.DateTimeFormat('fr-BE', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(d);
  return `${commune(s)}, semaine du ${jour}`;
}

/**
 * Les activités rangées sous leur type, prêtes à être affichées en sections.
 *
 * Le premier jet des cartes avait supprimé les intertitres « Plaines de
 * vacances » et « Stages à thème » au profit d'une grille plate. Le banc l'a
 * relevé, et il avait raison : ces deux mots-là sont deux recherches
 * distinctes, et une page qui ne les écrit nulle part ne répond à aucune des
 * deux. Le regroupement par activité et les intertitres ne s'excluent pas —
 * l'un range les cartes, l'autre les annonce.
 */
export function activitesParType(
  stages: StagePublic[],
): { type: TypeDActivite; titre: string; activites: Activite[] }[] {
  const activites = grouperParActivite(stages);
  return ORDRE.map((type) => ({
    type,
    titre: NOM_DU_TYPE_PLURIEL[type],
    activites: activites.filter((a) => a.type === type),
  })).filter((g) => g.activites.length > 0);
}
